import { useState } from 'react';

type Status = 'idle' | 'loading' | 'success' | 'error';

const FORM_ID = 'subscribe-reminder';
const SUBMIT_ADDR = 'https://readdy.ai/api/form/daijkhjoh653ivfvoek0';

export default function SubscribeCta() {
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('contact_alt') || '').trim();
    if (honeypot) {
      setStatus('success');
      setFormError('');
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'contact_alt') return;
      if (typeof value !== 'string') return;
      if (!value.trim()) return;
      payload.append(key, value);
    });

    setStatus('loading');
    setFormError('');

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText) as typeof parsed;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = /spam/i.test(serverMsg);
      const okCode = parsed?.code === 'OK';

      if (response.ok && okCode && !isSpam) {
        setStatus('success');
        setFormError('');
        form.reset();
      } else {
        setStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试。');
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，提交失败，请稍后重试。');
    }
  };

  return (
    <section id="subscribe" className="bg-background-50 pb-16 md:pb-20">
      <div className="mx-auto max-w-shell px-4 md:px-6">
        <div className="rounded-lg border border-primary-200 bg-primary-50 p-6 md:p-10">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_400px] lg:gap-12">
            <div>
              <span className="flex items-center gap-2 text-xs font-medium tracking-[0.2em] text-primary-600">
                <span className="h-px w-6 bg-primary-500"></span>
                GET NOTIFIED
              </span>
              <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                首发提醒，第一时间推给你
              </h2>
              <p className="mt-3 max-w-lg text-sm leading-relaxed text-foreground-700">
                留个邮箱，今日实时更新、刚刚发生的首发爆料一旦上线就通知你。不刷屏，只在真的有事的时候提醒一次。
              </p>
              <ul className="mt-6 flex flex-col gap-2 text-sm text-foreground-700">
                <li className="flex items-center gap-2">
                  <i className="ri-notification-3-line text-primary-600"></i>
                  新料上线即时提醒
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-timer-flash-line text-primary-600"></i>
                  每日一次今日重点汇总
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-shield-check-line text-primary-600"></i>
                  随时可退订，不会外传邮箱
                </li>
              </ul>
            </div>

            <form data-readdy-form="true" id={FORM_ID} onSubmit={handleSubmit} className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="sub-email" className="text-xs font-medium text-foreground-700">
                  邮箱地址
                </label>
                <input
                  id="sub-email"
                  type="email"
                  name="email"
                  required
                  placeholder="you@example.com"
                  className="rounded-md border border-background-300/70 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label htmlFor="sub-focus" className="text-xs font-medium text-foreground-700">
                  最想第一时间知道
                </label>
                <select
                  id="sub-focus"
                  name="focus"
                  className="rounded-md border border-background-300/70 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
                >
                  <option value="实时快讯">实时快讯</option>
                  <option value="首发爆料">首发爆料</option>
                  <option value="热评精选">热评精选</option>
                  <option value="全部都要">全部都要</option>
                </select>
              </div>

              <input
                type="text"
                name="contact_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="kaogao-cert-field"
              />

              <button
                type="submit"
                disabled={status === 'loading'}
                className="mt-1 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70 cursor-pointer"
              >
                {status === 'loading' ? '提交中…' : '开启首发提醒'}
              </button>

              {status === 'success' && (
                <p className="flex items-center gap-2 rounded-md border border-secondary-200 bg-secondary-50 px-3.5 py-2.5 text-xs text-secondary-900">
                  <i className="ri-checkbox-circle-line text-base"></i>
                  订阅成功，新的首发内容上线会第一时间通知你。
                </p>
              )}
              {status === 'error' && (
                <p className="flex items-center gap-2 rounded-md border border-primary-200 bg-primary-100 px-3.5 py-2.5 text-xs text-primary-800">
                  <i className="ri-error-warning-line text-base"></i>
                  {formError}
                </p>
              )}
              <p className="text-[11px] text-foreground-500">提交即表示同意接收免费看料的内容提醒。</p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}